import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import {
  AdminConfigOverview,
  AdminFeatureFlagHistory,
  AdminFeatureFlagItem,
  AdminGlobalConfigGroupItem,
  AdminGlobalConfigHistory,
} from '../types';

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const stringifyCompact = (value: unknown): string => {
  if (value === null || value === undefined) return '{}';

  try {
    const serialized = JSON.stringify(value);
    return serialized.length > 120 ? `${serialized.slice(0, 117)}...` : serialized;
  } catch {
    return '{}';
  }
};

const stringifyPretty = (value: unknown): string => {
  if (value === null || value === undefined) return '{}';

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

const readKycRequiredOnEntry = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return true;
  return (value as Record<string, unknown>).required_on_entry !== false;
};

const formatFlagChangeType = (value: 'SEED' | 'OVERRIDE' | 'TOGGLE' | 'ROLLOUT'): string => {
  switch (value) {
    case 'SEED':
      return 'Seed';
    case 'TOGGLE':
      return 'Toggle';
    case 'ROLLOUT':
      return 'Rollout';
    default:
      return 'Override';
  }
};

const formatGlobalChangeType = (value: 'SEED' | 'PATCH' | 'SYSTEM_SYNC'): string => {
  switch (value) {
    case 'SEED':
      return 'Seed';
    case 'SYSTEM_SYNC':
      return 'Sync';
    default:
      return 'Patch';
  }
};

const formatFlagVersionEntry = (item: AdminFeatureFlagHistory['history'][number]): string => {
  const parts = [
    `v${item.version_number}`,
    formatFlagChangeType(item.change_type),
    item.changed_by_name || item.changed_by_email || item.changed_by_user_id || 'Sistema',
  ];

  if (item.reason) {
    parts.push(item.reason);
  }

  parts.push(formatDateTime(item.created_at));
  return parts.join(' | ');
};

const formatGlobalVersionEntry = (item: AdminGlobalConfigHistory['history'][number]): string => {
  const parts = [
    `v${item.version_number}`,
    formatGlobalChangeType(item.change_type),
    item.changed_by_name || item.changed_by_email || item.changed_by_user_id || 'Sistema',
  ];

  if (item.reason) {
    parts.push(item.reason);
  }

  parts.push(formatDateTime(item.created_at));
  return parts.join(' | ');
};

const ConfigPage: React.FC = () => {
  const [payload, setPayload] = useState<AdminConfigOverview | null>(null);
  const [flagHistory, setFlagHistory] = useState<AdminFeatureFlagHistory | null>(null);
  const [globalHistory, setGlobalHistory] = useState<AdminGlobalConfigHistory | null>(null);
  const [selectedFlagKey, setSelectedFlagKey] = useState('');
  const [selectedGroupKey, setSelectedGroupKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [flagHistoryLoading, setFlagHistoryLoading] = useState(false);
  const [globalHistoryLoading, setGlobalHistoryLoading] = useState(false);
  const [flagSaveLoading, setFlagSaveLoading] = useState(false);
  const [globalSaveLoading, setGlobalSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [flagHistoryError, setFlagHistoryError] = useState('');
  const [globalHistoryError, setGlobalHistoryError] = useState('');
  const [flagSaveError, setFlagSaveError] = useState('');
  const [globalSaveError, setGlobalSaveError] = useState('');
  const [flagEnabled, setFlagEnabled] = useState(true);
  const [flagRolloutJson, setFlagRolloutJson] = useState('{}');
  const [flagReason, setFlagReason] = useState('');
  const [globalConfigJson, setGlobalConfigJson] = useState('{}');
  const [globalReason, setGlobalReason] = useState('');
  const [kycRequiredOnEntry, setKycRequiredOnEntry] = useState(true);
  const [kycReason, setKycReason] = useState('');
  const [kycSaveLoading, setKycSaveLoading] = useState(false);
  const [kycSaveError, setKycSaveError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const nextPayload = await adminService.getConfigOverview();
      setPayload(nextPayload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar a governanca global');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFlagHistory = useCallback(async (flagKey: string) => {
    setSelectedFlagKey(flagKey);
    setFlagHistoryLoading(true);
    setFlagHistoryError('');
    setFlagSaveError('');
    setFlagReason('');

    try {
      const nextHistory = await adminService.getFeatureFlagHistory(flagKey);
      setFlagHistory(nextHistory);
      setFlagEnabled(nextHistory.flag.enabled);
      setFlagRolloutJson(stringifyPretty(nextHistory.flag.rollout_json));
    } catch (nextError: any) {
      setFlagHistory(null);
      setFlagHistoryError(nextError.message || 'Erro ao carregar o historico da feature flag');
    } finally {
      setFlagHistoryLoading(false);
    }
  }, []);

  const loadGlobalHistory = useCallback(async (groupKey: string) => {
    setSelectedGroupKey(groupKey);
    setGlobalHistoryLoading(true);
    setGlobalHistoryError('');
    setGlobalSaveError('');
    setGlobalReason('');

    try {
      const nextHistory = await adminService.getGlobalConfigHistory(groupKey);
      setGlobalHistory(nextHistory);
      setGlobalConfigJson(stringifyPretty(nextHistory.group.config_json));
    } catch (nextError: any) {
      setGlobalHistory(null);
      setGlobalHistoryError(nextError.message || 'Erro ao carregar o historico do grupo global');
    } finally {
      setGlobalHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedFlagKey && payload?.feature_flags.items.length) {
      void loadFlagHistory(payload.feature_flags.items[0].flag_key);
    }
  }, [loadFlagHistory, payload, selectedFlagKey]);

  useEffect(() => {
    if (!selectedGroupKey && payload?.global_config.items.length) {
      void loadGlobalHistory(payload.global_config.items[0].group_key);
    }
  }, [loadGlobalHistory, payload, selectedGroupKey]);

  const currentKycRequiredOnEntry = useMemo(() => {
    const group = payload?.global_config.items.find((item) => item.group_key === 'KYC_POLICY');
    return readKycRequiredOnEntry(group?.config_json);
  }, [payload]);

  useEffect(() => {
    setKycRequiredOnEntry(currentKycRequiredOnEntry);
  }, [currentKycRequiredOnEntry]);

  const metricCards = useMemo(() => {
    const flagSummary = payload?.feature_flags.summary;
    const globalSummary = payload?.global_config.summary;

    return [
      {
        eyebrow: 'Flags',
        title: 'Total',
        value: String(flagSummary?.total_flags || 0),
        tone: 'blue' as const,
      },
      {
        eyebrow: 'Flags',
        title: 'Ativas',
        value: String(flagSummary?.enabled_flags || 0),
        tone: 'emerald' as const,
      },
      {
        eyebrow: 'Config',
        title: 'Grupos globais',
        value: String(globalSummary?.total_groups || 0),
        tone: 'amber' as const,
      },
      {
        eyebrow: 'Audit',
        title: 'Schema',
        value: payload?.audit_governance.schema_version || 'v1',
        tone: 'slate' as const,
      },
    ];
  }, [payload]);

  const handleRefresh = async () => {
    await loadData();

    if (selectedGroupKey) {
      await loadGlobalHistory(selectedGroupKey);
    }

    if (selectedFlagKey) {
      await loadFlagHistory(selectedFlagKey);
    }
  };

  const handleSaveGlobal = async () => {
    if (!globalHistory) return;

    const trimmedReason = globalReason.trim();
    if (!trimmedReason) {
      setGlobalSaveError('Informe o motivo obrigatorio para governar este grupo global.');
      return;
    }

    let parsedConfig: unknown = {};
    try {
      parsedConfig = globalConfigJson.trim() ? JSON.parse(globalConfigJson) : {};
    } catch {
      setGlobalSaveError('Config JSON invalido para a governanca global.');
      return;
    }

    setGlobalSaveLoading(true);
    setGlobalSaveError('');

    try {
      const nextHistory = await adminService.patchGlobalConfig({
        groupKey: globalHistory.group.group_key,
        configJson: parsedConfig,
        reason: trimmedReason,
      });

      setGlobalHistory(nextHistory);
      setSelectedGroupKey(nextHistory.group.group_key);
      setGlobalConfigJson(stringifyPretty(nextHistory.group.config_json));
      setGlobalReason('');
      await loadData();
    } catch (nextError: any) {
      setGlobalSaveError(nextError.message || 'Erro ao governar a config global');
    } finally {
      setGlobalSaveLoading(false);
    }
  };

  const handleSaveFlag = async () => {
    if (!flagHistory) return;

    const trimmedReason = flagReason.trim();
    if (!trimmedReason) {
      setFlagSaveError('Informe o motivo obrigatorio para governar esta feature flag.');
      return;
    }

    let parsedRollout: unknown = {};
    try {
      parsedRollout = flagRolloutJson.trim() ? JSON.parse(flagRolloutJson) : {};
    } catch {
      setFlagSaveError('Rollout JSON invalido para a governanca da feature flag.');
      return;
    }

    setFlagSaveLoading(true);
    setFlagSaveError('');

    try {
      const nextHistory = await adminService.overrideFeatureFlag(flagHistory.flag.flag_key, {
        enabled: flagEnabled,
        rolloutJson: parsedRollout,
        reason: trimmedReason,
      });

      setFlagHistory(nextHistory);
      setSelectedFlagKey(nextHistory.flag.flag_key);
      setFlagEnabled(nextHistory.flag.enabled);
      setFlagRolloutJson(stringifyPretty(nextHistory.flag.rollout_json));
      setFlagReason('');
      await loadData();
    } catch (nextError: any) {
      setFlagSaveError(nextError.message || 'Erro ao governar a feature flag');
    } finally {
      setFlagSaveLoading(false);
    }
  };

  const handleSaveKycPolicy = async () => {
    const trimmedReason = kycReason.trim();
    if (!trimmedReason) {
      setKycSaveError('Informe o motivo obrigatorio para alterar a exigencia de KYC.');
      return;
    }

    setKycSaveLoading(true);
    setKycSaveError('');
    try {
      const nextHistory = await adminService.patchGlobalConfig({
        groupKey: 'KYC_POLICY',
        configJson: { required_on_entry: kycRequiredOnEntry },
        reason: trimmedReason,
      });
      setKycReason('');
      setGlobalHistory(nextHistory);
      setSelectedGroupKey(nextHistory.group.group_key);
      setGlobalConfigJson(stringifyPretty(nextHistory.group.config_json));
      await loadData();
    } catch (nextError: any) {
      setKycSaveError(nextError.message || 'Nao foi possivel salvar a politica de KYC');
    } finally {
      setKycSaveLoading(false);
    }
  };

  const currentFlag: AdminFeatureFlagItem | null = flagHistory?.flag || null;
  const currentGroup: AdminGlobalConfigGroupItem | null = globalHistory?.group || null;

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Config</p>
          <h1>Governanca global do SaaS</h1>
          <p>
            A etapa atual libera dois niveis de governanca auditada: grupos globais do SaaS e
            feature flags. A configuracao operacional de club continua fora deste modulo.
          </p>
        </div>
        <button
          type="button"
          className="admin-secondary-button"
          onClick={() => void handleRefresh()}
          disabled={
            loading ||
            flagHistoryLoading ||
            globalHistoryLoading ||
            flagSaveLoading ||
            globalSaveLoading
          }
        >
          {loading || flagHistoryLoading || globalHistoryLoading || flagSaveLoading || globalSaveLoading
            ? 'Atualizando...'
            : 'Atualizar'}
        </button>
      </section>

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}
      {globalHistoryError ? <div className="admin-banner admin-banner--error">{globalHistoryError}</div> : null}
      {globalSaveError ? <div className="admin-banner admin-banner--error">{globalSaveError}</div> : null}
      {kycSaveError ? <div className="admin-banner admin-banner--error">{kycSaveError}</div> : null}
      {flagHistoryError ? <div className="admin-banner admin-banner--error">{flagHistoryError}</div> : null}
      {flagSaveError ? <div className="admin-banner admin-banner--error">{flagSaveError}</div> : null}

      {payload ? (
        <>
          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Entrada de jogadores</p>
                  <h2>KYC obrigatorio na entrada</h2>
                </div>
                <span className="admin-chip">{currentKycRequiredOnEntry ? 'ATIVO' : 'DESATIVADO'}</span>
              </div>

              <p className="admin-note-chip">
                {currentKycRequiredOnEntry
                  ? 'Novos jogadores e contas pendentes precisam concluir KYC para acessar areas protegidas.'
                  : 'O backend libera o acesso de contas elegiveis sem reescrever o historico KYC existente.'}
              </p>

              <div className="admin-filter-grid" style={{ marginTop: 18 }}>
                <label className="admin-filter-field">
                  <span>Exigir KYC na entrada</span>
                  <select
                    className="admin-search-input"
                    value={kycRequiredOnEntry ? 'true' : 'false'}
                    onChange={(event) => setKycRequiredOnEntry(event.target.value === 'true')}
                    disabled={kycSaveLoading}
                  >
                    <option value="true">SIM — exigir verificacao</option>
                    <option value="false">NAO — liberar sem KYC</option>
                  </select>
                </label>

                <label className="admin-filter-field">
                  <span>Motivo obrigatorio</span>
                  <textarea
                    className="admin-search-input"
                    rows={3}
                    value={kycReason}
                    onChange={(event) => setKycReason(event.target.value)}
                    placeholder="Registre o motivo da alteracao para auditoria."
                    disabled={kycSaveLoading}
                  />
                </label>
              </div>

              <div className="admin-filter-actions">
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={() => void handleSaveKycPolicy()}
                  disabled={kycSaveLoading || kycRequiredOnEntry === currentKycRequiredOnEntry}
                >
                  {kycSaveLoading ? 'Salvando...' : 'Salvar politica KYC'}
                </button>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Comportamento seguro</p>
                  <h2>Reversivel e auditado</h2>
                </div>
              </div>
              <div className="admin-note-stack">
                <div className="admin-note-chip">Desligar a politica nao altera nem apaga sessoes, eventos ou decisoes KYC ja registradas.</div>
                <div className="admin-note-chip">Contas bloqueadas por menoridade ou KYC reprovado continuam bloqueadas.</div>
                <div className="admin-note-chip">Religar a politica volta a exigir KYC para contas ainda pendentes.</div>
              </div>
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Config global</p>
                  <h2>Grupos do SaaS</h2>
                </div>
                <span className="admin-chip">
                  Atualizado em {formatDateTime(payload.global_config.generated_at)}
                </span>
              </div>

              <div className="admin-kpi-grid">
                <article className="admin-kpi-item">
                  <span>Persistidos</span>
                  <strong>{payload.global_config.summary.persisted_groups}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Fallback derivado</span>
                  <strong>{payload.global_config.summary.derived_groups}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Atualizados 7d</span>
                  <strong>{payload.global_config.summary.updated_last_7d}</strong>
                </article>
              </div>

              <div className="admin-table-wrap" style={{ marginTop: 18 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th>Origem</th>
                      <th>Atualizado</th>
                      <th>Governanca</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.global_config.items.length ? (
                      payload.global_config.items.map((group) => (
                        <tr
                          key={group.group_key}
                          className={
                            selectedGroupKey === group.group_key ? 'admin-table__row--selected' : ''
                          }
                        >
                          <td>
                            <strong>{group.title}</strong>
                            <span>{group.description}</span>
                          </td>
                          <td>
                            <strong>{group.source_type}</strong>
                            <span>{stringifyCompact(group.config_json)}</span>
                          </td>
                          <td>
                            <strong>{formatDateTime(group.updated_at)}</strong>
                            <span>{group.updated_by_name || group.updated_by_user_id || '-'}</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="admin-link-chip admin-link-chip--button"
                              onClick={() => void loadGlobalHistory(group.group_key)}
                              disabled={globalHistoryLoading && selectedGroupKey === group.group_key}
                            >
                              {globalHistoryLoading && selectedGroupKey === group.group_key
                                ? 'Abrindo...'
                                : 'Governar'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>Nenhum grupo global foi carregado ainda.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Governanca global</p>
                  <h2>{currentGroup?.title || 'Selecione um grupo global'}</h2>
                </div>
                <span className="admin-chip">{currentGroup?.source_type || 'Sem foco ativo'}</span>
              </div>

              {currentGroup ? (
                <>
                  <div className="admin-kpi-grid">
                    <article className="admin-kpi-item">
                      <span>Grupo</span>
                      <strong>{currentGroup.group_key}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Versoes</span>
                      <strong>{globalHistory?.history.length || 0}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Atualizado</span>
                      <strong>{formatDateTime(currentGroup.updated_at)}</strong>
                    </article>
                  </div>

                  <div className="admin-detail-grid">
                    <div className="admin-detail-pair">
                      <span>Descricao</span>
                      <strong>{currentGroup.description}</strong>
                    </div>
                    <div className="admin-detail-pair">
                      <span>Atualizado por</span>
                      <strong>{currentGroup.updated_by_name || currentGroup.updated_by_user_id || '-'}</strong>
                    </div>
                  </div>

                  <div className="admin-filter-grid" style={{ marginTop: 18 }}>
                    <label className="admin-filter-field">
                      <span>Config JSON</span>
                      <textarea
                        className="admin-search-input"
                        rows={10}
                        value={globalConfigJson}
                        onChange={(event) => setGlobalConfigJson(event.target.value)}
                      />
                    </label>

                    <label className="admin-filter-field">
                      <span>Motivo obrigatorio</span>
                      <textarea
                        className="admin-search-input"
                        rows={4}
                        value={globalReason}
                        onChange={(event) => setGlobalReason(event.target.value)}
                        placeholder="Explique por que este grupo global esta sendo alterado."
                      />
                    </label>
                  </div>

                  <div className="admin-filter-actions">
                    <button
                      type="button"
                      className="admin-secondary-button"
                      onClick={() => void handleSaveGlobal()}
                      disabled={globalSaveLoading}
                    >
                      {globalSaveLoading ? 'Salvando...' : 'Salvar config global'}
                    </button>
                    <button
                      type="button"
                      className="admin-secondary-button"
                      onClick={() => void loadGlobalHistory(currentGroup.group_key)}
                      disabled={globalHistoryLoading || globalSaveLoading}
                    >
                      {globalHistoryLoading ? 'Recarregando...' : 'Recarregar historico'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="admin-note-chip">
                  {globalHistoryLoading
                    ? 'Carregando grupo global...'
                    : 'Selecione um grupo do inventario para abrir a governanca do SaaS.'}
                </div>
              )}
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Historico global</p>
                  <h2>Versoes do grupo do SaaS</h2>
                </div>
                <span className="admin-chip">{globalHistory?.history.length || 0} versao(oes)</span>
              </div>

              <div className="admin-note-stack">
                {globalHistory?.history.length ? (
                  globalHistory.history.map((item) => (
                    <div key={item.id} className="admin-note-chip">
                      {formatGlobalVersionEntry(item)}
                    </div>
                  ))
                ) : (
                  <div className="admin-note-chip">
                    {globalHistoryLoading
                      ? 'Carregando historico global...'
                      : 'Nenhuma versao foi registrada ainda para o grupo global em foco.'}
                  </div>
                )}
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Audit envelope</p>
                  <h2>Contrato obrigatorio para mutacoes sensiveis</h2>
                </div>
                <span className="admin-chip">
                  Bloqueio:{' '}
                  {payload.audit_governance.blocked_mutations_without_audit ? 'ativo' : 'inativo'}
                </span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Schema</span>
                  <strong>{payload.audit_governance.schema_version}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Campos obrigatorios</span>
                  <strong>{payload.audit_governance.required_fields.length}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Campos recomendados</span>
                  <strong>{payload.audit_governance.recommended_context_fields.length}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Mutacoes sensiveis</span>
                  <strong>
                    {payload.audit_governance.blocked_mutations_without_audit
                      ? 'BLOQUEADAS SEM AUDIT'
                      : 'LIBERADAS'}
                  </strong>
                </div>
              </div>

              <div className="admin-note-stack" style={{ marginTop: 18 }}>
                {payload.audit_governance.required_fields.map((field) => (
                  <div key={field} className="admin-note-chip">
                    Obrigatorio: {field}
                  </div>
                ))}
              </div>

              <div className="admin-note-stack" style={{ marginTop: 12 }}>
                {payload.audit_governance.recommended_context_fields.map((field) => (
                  <div key={field} className="admin-note-chip">
                    Recomendado: {field}
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Feature flags</p>
                  <h2>Inventario governado</h2>
                </div>
                <span className="admin-chip">
                  Atualizado em {formatDateTime(payload.feature_flags.generated_at)}
                </span>
              </div>

              <div className="admin-kpi-grid">
                <article className="admin-kpi-item">
                  <span>Total de escopos</span>
                  <strong>{payload.feature_flags.summary.scopes.length}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Atualizadas 7d</span>
                  <strong>{payload.feature_flags.summary.updated_last_7d}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Modo da etapa</span>
                  <strong>FLAG_GOVERNANCE</strong>
                </article>
              </div>

              <div className="admin-table-wrap" style={{ marginTop: 18 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Flag</th>
                      <th>Escopo</th>
                      <th>Estado</th>
                      <th>Atualizado</th>
                      <th>Governanca</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.feature_flags.items.length ? (
                      payload.feature_flags.items.map((flag) => (
                        <tr
                          key={flag.flag_key}
                          className={
                            selectedFlagKey === flag.flag_key ? 'admin-table__row--selected' : ''
                          }
                        >
                          <td>
                            <strong>{flag.flag_key}</strong>
                            <span>{flag.description}</span>
                          </td>
                          <td>{flag.scope}</td>
                          <td>
                            <strong>{flag.enabled ? 'ENABLED' : 'DISABLED'}</strong>
                            <span>{stringifyCompact(flag.rollout_json)}</span>
                          </td>
                          <td>
                            <strong>{formatDateTime(flag.updated_at)}</strong>
                            <span>{flag.updated_by_name || flag.updated_by_user_id || '-'}</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="admin-link-chip admin-link-chip--button"
                              onClick={() => void loadFlagHistory(flag.flag_key)}
                              disabled={flagHistoryLoading && selectedFlagKey === flag.flag_key}
                            >
                              {flagHistoryLoading && selectedFlagKey === flag.flag_key
                                ? 'Abrindo...'
                                : 'Governar'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>Nenhuma feature flag encontrada no banco conectado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Governanca de flags</p>
                  <h2>{currentFlag?.flag_key || 'Selecione uma feature flag'}</h2>
                </div>
                <span className="admin-chip">
                  {currentFlag ? (currentFlag.enabled ? 'ENABLED' : 'DISABLED') : 'Sem foco ativo'}
                </span>
              </div>

              {currentFlag ? (
                <>
                  <div className="admin-kpi-grid">
                    <article className="admin-kpi-item">
                      <span>Escopo</span>
                      <strong>{currentFlag.scope}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Versoes</span>
                      <strong>{flagHistory?.history.length || 0}</strong>
                    </article>
                    <article className="admin-kpi-item">
                      <span>Atualizado</span>
                      <strong>{formatDateTime(currentFlag.updated_at)}</strong>
                    </article>
                  </div>

                  <div className="admin-detail-grid">
                    <div className="admin-detail-pair">
                      <span>Descricao</span>
                      <strong>{currentFlag.description}</strong>
                    </div>
                    <div className="admin-detail-pair">
                      <span>Atualizado por</span>
                      <strong>{currentFlag.updated_by_name || currentFlag.updated_by_user_id || '-'}</strong>
                    </div>
                  </div>

                  <div className="admin-filter-grid" style={{ marginTop: 18 }}>
                    <label className="admin-filter-field">
                      <span>Estado enabled</span>
                      <select
                        className="admin-search-input"
                        value={flagEnabled ? 'true' : 'false'}
                        onChange={(event) => setFlagEnabled(event.target.value === 'true')}
                      >
                        <option value="true">ENABLED</option>
                        <option value="false">DISABLED</option>
                      </select>
                    </label>

                    <label className="admin-filter-field">
                      <span>Rollout JSON</span>
                      <textarea
                        className="admin-search-input"
                        rows={8}
                        value={flagRolloutJson}
                        onChange={(event) => setFlagRolloutJson(event.target.value)}
                      />
                    </label>

                    <label className="admin-filter-field">
                      <span>Motivo obrigatorio</span>
                      <textarea
                        className="admin-search-input"
                        rows={4}
                        value={flagReason}
                        onChange={(event) => setFlagReason(event.target.value)}
                        placeholder="Explique por que esta alteracao de flag esta sendo aplicada."
                      />
                    </label>
                  </div>

                  <div className="admin-filter-actions">
                    <button
                      type="button"
                      className="admin-secondary-button"
                      onClick={() => void handleSaveFlag()}
                      disabled={flagSaveLoading}
                    >
                      {flagSaveLoading ? 'Salvando...' : 'Salvar governanca'}
                    </button>
                    <button
                      type="button"
                      className="admin-secondary-button"
                      onClick={() => void loadFlagHistory(currentFlag.flag_key)}
                      disabled={flagHistoryLoading || flagSaveLoading}
                    >
                      {flagHistoryLoading ? 'Recarregando...' : 'Recarregar historico'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="admin-note-chip">
                  {flagHistoryLoading
                    ? 'Carregando feature flag...'
                    : 'Selecione uma flag do inventario para abrir a governanca.'}
                </div>
              )}
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Historico de flags</p>
                  <h2>Versoes da feature flag</h2>
                </div>
                <span className="admin-chip">{flagHistory?.history.length || 0} versao(oes)</span>
              </div>

              <div className="admin-note-stack">
                {flagHistory?.history.length ? (
                  flagHistory.history.map((item) => (
                    <div key={item.id} className="admin-note-chip">
                      {formatFlagVersionEntry(item)}
                    </div>
                  ))
                ) : (
                  <div className="admin-note-chip">
                    {flagHistoryLoading
                      ? 'Carregando historico...'
                      : 'Nenhuma versao foi registrada ainda para a feature flag em foco.'}
                  </div>
                )}
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Observacoes</p>
                  <h2>Limites e orientacoes desta etapa</h2>
                </div>
              </div>

              <div className="admin-note-stack">
                {payload.global_config.notes.map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
                {(globalHistory?.notes || []).map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
                {payload.feature_flags.notes.map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
                {(flagHistory?.notes || []).map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
                {payload.audit_governance.notes.map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
                {payload.notes.map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
              </div>
            </article>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default ConfigPage;
